import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPencil,
  faPlus,
  faSearch,
  faTrash,
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import Swal from "sweetalert2";
import { API_URL } from "../../config";
import LoadingSpinner from "../../components/LoadingSpinner";
import { PermissionWrapper, hasPermission } from "../../helpers/permission.js";
import { getEventDisplay } from "../../helpers/workflowHelper";

function WorkflowOrder() {
  const [activeTab, setActiveTab] = useState("tradepro"); // "tradepro" or "streetfolio"

  // Tab-specific states
  const [tradeproData, setTradeproData] = useState({
    workflows: [],
    loading: false,
    error: null,
    workflowNameFilter: "",
    eventFilter: "",
    page: 1,
    pageCount: 1,
    total: 0
  });

  const [streetfolioData, setStreetfolioData] = useState({
    workflows: [],
    loading: false,
    error: null,
    workflowNameFilter: "",
    eventFilter: "",
    page: 1,
    pageCount: 1,
    total: 0
  });

  const limit = 100;
  const token = localStorage.getItem("authToken");

  // Fetch Tradepro data
  const fetchTradeproWorkflows = async () => {
    try {
      setTradeproData(prev => ({ ...prev, loading: true }));
      const response = await axios.get(`${API_URL}workflowOrder`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      setTradeproData(prev => ({
        ...prev,
        workflows: response.data.data.data || [],
        total: (response.data.data.data || []).length,
        pageCount: Math.ceil((response.data.data.data || []).length / limit),
        loading: false
      }));
    } catch (error) {
      setTradeproData(prev => ({
        ...prev,
        error: error.message,
        loading: false
      }));
    }
  };

  // Fetch Streetfolio data
  const fetchStreetfolioWorkflows = async () => {
    try {
      setStreetfolioData(prev => ({ ...prev, loading: true }));
      const response = await axios.get(`${API_URL}workflowOrderStreet`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      setStreetfolioData(prev => ({
        ...prev,
        workflows: response.data.data.data || [],
        total: (response.data.data.data || []).length,
        pageCount: Math.ceil((response.data.data.data || []).length / limit),
        loading: false
      }));
    } catch (error) {
      setStreetfolioData(prev => ({
        ...prev,
        error: error.message,
        loading: false
      }));
    }
  };

  useEffect(() => {
    fetchTradeproWorkflows();
    fetchStreetfolioWorkflows();
  }, []);

  // Filter functions for both tabs
  const filteredTradeproWorkflows = useMemo(() => {
    return tradeproData.workflows.filter((workflow) => {
      if (
        tradeproData.workflowNameFilter &&
        !(workflow.workflow_name || "")
          .toLowerCase()
          .includes(tradeproData.workflowNameFilter.toLowerCase())
      ) {
        return false;
      }
      if (
        tradeproData.eventFilter &&
        !getEventDisplay(workflow)
          .toLowerCase()
          .includes(tradeproData.eventFilter.toLowerCase())
      ) {
        return false;
      }
      return true;
    });
  }, [tradeproData.workflows, tradeproData.workflowNameFilter, tradeproData.eventFilter]);

  const filteredStreetfolioWorkflows = useMemo(() => {
    return streetfolioData.workflows.filter((workflow) => {
      if (
        streetfolioData.workflowNameFilter &&
        !(workflow.workflow_name || "")
          .toLowerCase()
          .includes(streetfolioData.workflowNameFilter.toLowerCase())
      ) {
        return false;
      }
      if (
        streetfolioData.eventFilter &&
        !getEventDisplay(workflow)
          .toLowerCase()
          .includes(streetfolioData.eventFilter.toLowerCase())
      ) {
        return false;
      }
      return true;
    });
  }, [streetfolioData.workflows, streetfolioData.workflowNameFilter, streetfolioData.eventFilter]);

  // Update pagination when filters change
  useEffect(() => {
    setTradeproData(prev => ({
      ...prev,
      total: filteredTradeproWorkflows.length,
      pageCount: Math.ceil(filteredTradeproWorkflows.length / limit),
      page: 1
    }));
  }, [filteredTradeproWorkflows, limit]);

  useEffect(() => {
    setStreetfolioData(prev => ({
      ...prev,
      total: filteredStreetfolioWorkflows.length,
      pageCount: Math.ceil(filteredStreetfolioWorkflows.length / limit),
      page: 1
    }));
  }, [filteredStreetfolioWorkflows, limit]);

  useEffect(() => {
    if (activeTab === "tradepro") {
      setTradeproData(prev => ({ ...prev, loading: true }));
      setTimeout(() => {
        setTradeproData(prev => ({ ...prev, loading: false }));
      }, 300);
    }

    if (activeTab === "streetfolio") {
      setStreetfolioData(prev => ({ ...prev, loading: true }));
      setTimeout(() => {
        setStreetfolioData(prev => ({ ...prev, loading: false }));
      }, 300);
    }
  }, [activeTab]);


  // Pagination handlers
  const handleTradeproPageChange = (newPage) => {
    if (newPage >= 1 && newPage <= tradeproData.pageCount) {
      setTradeproData(prev => ({ ...prev, page: newPage }));
    }
  };

  const handleStreetfolioPageChange = (newPage) => {
    if (newPage >= 1 && newPage <= streetfolioData.pageCount) {
      setStreetfolioData(prev => ({ ...prev, page: newPage }));
    }
  };

  // Delete handlers
  const handleTradeproDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You will not be able to recover this record!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonClass: "btn-danger",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, cancel!",
    });

    if (result.isConfirmed) {
      try {
        const response = await axios.delete(`${API_URL}workflowOrder/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        setTradeproData(prev => ({
          ...prev,
          workflows: prev.workflows.filter((dt) => dt.id !== id)
        }));

        Swal.fire(
          "Deleted!",
          response.data.message || "Your record has been deleted.",
          "success"
        );
      } catch (error) {
        Swal.fire(
          "Error",
          error.response?.data?.message || "Something went wrong while deleting.",
          "error"
        );
      }
    }
  };

  const handleStreetfolioDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You will not be able to recover this record!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonClass: "btn-danger",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, cancel!",
    });

    if (result.isConfirmed) {
      try {
        const response = await axios.delete(
          `${API_URL}workflowOrderStreet/${id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        setStreetfolioData(prev => ({
          ...prev,
          workflows: prev.workflows.filter((dt) => dt.id !== id)
        }));

        Swal.fire(
          "Deleted!",
          response.data.message || "Your record has been deleted.",
          "success"
        );
      } catch (error) {
        Swal.fire(
          "Error",
          error.response?.data?.message || "Something went wrong while deleting.",
          "error"
        );
      }
    }
  };

  // Helper functions to get paginated data
  const getTradeproPaginatedData = () => {
    const start = (tradeproData.page - 1) * limit + 1;
    const end = Math.min(tradeproData.page * limit, tradeproData.total);
    const paginatedData = filteredTradeproWorkflows.slice(start - 1, end);
    return { start, end, paginatedData };
  };

  const getStreetfolioPaginatedData = () => {
    const start = (streetfolioData.page - 1) * limit + 1;
    const end = Math.min(streetfolioData.page * limit, streetfolioData.total);
    const paginatedData = filteredStreetfolioWorkflows.slice(start - 1, end);
    return { start, end, paginatedData };
  };

  const { start: tradeproStart, end: tradeproEnd, paginatedData: tradeproPaginated } = getTradeproPaginatedData();
  const { start: streetfolioStart, end: streetfolioEnd, paginatedData: streetfolioPaginated } = getStreetfolioPaginatedData();

  // Update filter handlers
  const updateTradeproFilter = (field, value) => {
    setTradeproData(prev => ({
      ...prev,
      loading: true,
      [field]: value
    }));

    setTimeout(() => {
      setTradeproData(prev => ({ ...prev, loading: false }));
    }, 300);
  };

  const updateStreetfolioFilter = (field, value) => {
    setStreetfolioData(prev => ({
      ...prev,
      loading: true,
      [field]: value
    }));

    setTimeout(() => {
      setStreetfolioData(prev => ({ ...prev, loading: false }));
    }, 300);
  };

  const clearTradeproFilters = () => {
    setTradeproData(prev => ({
      ...prev,
      workflowNameFilter: "",
      eventFilter: ""
    }));
  };

  const clearStreetfolioFilters = () => {
    setStreetfolioData(prev => ({
      ...prev,
      workflowNameFilter: "",
      eventFilter: ""
    }));
  };

  useEffect(() => {
    const handleClearFilters = () => {
      clearTradeproFilters();
      clearStreetfolioFilters();
    };
    window.addEventListener('clearWorkFlowOrderFilterEvent', handleClearFilters);

    return () => {
      window.removeEventListener('clearWorkFlowOrderFilterEvent', handleClearFilters);
    };
  }, []);

  return (
    <div className="page-container page-header-fixed page-sidebar-fixed fade in mt-5">
      {/* {(tradeproData.loading || streetfolioData.loading) && <LoadingSpinner />} */}
      <div className="">
        <div className="panel panel-default with-rounded-corner with-shadow p-10">
          <div className="panel-heading border-0">
            <div className="row">
              <div className="col-md-6">
                <h4 className="panel-title pull-left">Manage Order Workflow</h4>
              </div>
              <div className="col-md-6" style={{ textAlign: "right" }}>
                {/* Sorting Workflow Button */}
                {activeTab === "tradepro" && (
                  <Link
                    to="/Workflow/Order/view"
                    className="btn btn-default btn-sm mx-1"
                  >
                    <FontAwesomeIcon icon={faSearch} /> Sorting Workflow
                  </Link>
                )}
                {activeTab === "streetfolio" && (
                  <Link
                    to="/Workflow/Order/view"
                    className="btn btn-default btn-sm mx-1"
                  >
                    <FontAwesomeIcon icon={faSearch} /> Sorting Workflow
                  </Link>
                )}

                {/* Add Workflow Button */}
                <PermissionWrapper module="workflow_order" action="add">
                  {activeTab === "tradepro" && (
                    <Link
                      to="/WorkflowTradepro/Order/add"
                      className="btn btn-default btn-sm"
                    >
                      <FontAwesomeIcon icon={faPlus} /> Add Workflow
                    </Link>
                  )}
                  {activeTab === "streetfolio" && (
                    <Link
                      to="/WorkflowStreetfolio/Order/add"
                      className="btn btn-default btn-sm"
                    >
                      <FontAwesomeIcon icon={faPlus} /> Add Workflow
                    </Link>
                  )}
                </PermissionWrapper>

                {/* Pagination Controls - Show based on active tab */}
                {activeTab === "tradepro" && tradeproData.workflows.length > 0 && (
                  <div className="d-inline-block ms-2">
                    <span style={{ fontSize: "0.8rem" }} className="me-2">
                      {tradeproStart} to {tradeproEnd} of {tradeproData.total}
                    </span>
                    <button
                      className="btn btn-default btn-sm"
                      title="Previous"
                      onClick={() => handleTradeproPageChange(tradeproData.page - 1)}
                      disabled={tradeproData.page === 1}
                    >
                      &lt;
                    </button>
                    &nbsp;
                    <button
                      className="btn btn-default btn-sm"
                      title="Next"
                      onClick={() => handleTradeproPageChange(tradeproData.page + 1)}
                      disabled={tradeproData.page === tradeproData.pageCount}
                    >
                      &gt;
                    </button>
                  </div>
                )}
                {activeTab === "streetfolio" && streetfolioData.workflows.length > 0 && (
                  <div className="d-inline-block ms-2">
                    <span style={{ fontSize: "0.8rem" }} className="me-2">
                      {streetfolioStart} to {streetfolioEnd} of {streetfolioData.total}
                    </span>
                    <button
                      className="btn btn-default btn-sm"
                      title="Previous"
                      onClick={() => handleStreetfolioPageChange(streetfolioData.page - 1)}
                      disabled={streetfolioData.page === 1}
                    >
                      &lt;
                    </button>
                    &nbsp;
                    <button
                      className="btn btn-default btn-sm"
                      title="Next"
                      onClick={() => handleStreetfolioPageChange(streetfolioData.page + 1)}
                      disabled={streetfolioData.page === streetfolioData.pageCount}
                    >
                      &gt;
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="panel panel-default with-rounded-corner with-shadow mb-3">
            <div className="panel-body">
              <div className="row">
                <div className="col-md-12">
                  <label className="me-3">Select Workflow Type:</label>
                  <div className="btn-group" role="group">
                    <button
                      type="button"
                      className={`btn ${activeTab === "tradepro" ? "btn-primary" : "btn-default"}`}
                      onClick={() => setActiveTab("tradepro")}
                    >
                      Tradepro Workflows
                    </button>
                    <button
                      type="button"
                      className={`btn ${activeTab === "streetfolio" ? "btn-primary" : "btn-default"}`}
                      onClick={() => setActiveTab("streetfolio")}
                    >
                      Streetfolio Workflows
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Tradepro Tab Content */}
          {activeTab === "tradepro" && (
            <>
              {tradeproData.workflows.length > 0 || tradeproData.loading ?(
                <div className="table-responsive sticky-table-container">
                  <style>
                    {`
                      .sticky-seo-table thead tr:first-child th {
                        position: sticky;
                        top: 0;
                        z-index: 20;
                        background: #f8f9fa;
                       }

                      .sticky-seo-table thead tr:nth-child(2) th {
                        position: sticky;
                        top: 25px; /* Adjust if needed based on first header height */
                        z-index: 19;
                        background: white;
                       }

                      .sticky-table-container {
                        max-height: calc(100vh - 268px); /* Adjust height if needed */
                        overflow: auto;
                       }

                      .sticky-seo-table th {
                        vertical-align: top;
                       }
                    `}
                  </style>
                  <table className="table table-condensed table-bordered bg-white sticky-seo-table">
                    <thead>
                      <tr>
                        <th style={{ textAlign: "center" }}>#</th>
                        <th>Workflow Name</th>
                        <th>Event</th>
                        <th style={{ width: "1%" }}>Edit</th>
                        <th style={{ width: "1%" }}>Delete</th>
                      </tr>
                      <tr>
                        <th style={{ textAlign: "center" }}></th>
                        <th>
                          <input
                            type="text"
                            className="form-control input-sm"
                            placeholder="Search workflow name..."
                            value={tradeproData.workflowNameFilter}
                            onChange={(e) => updateTradeproFilter("workflowNameFilter", e.target.value)}
                          />
                        </th>
                        <th>
                          <input
                            type="text"
                            className="form-control input-sm"
                            placeholder="Search event..."
                            value={tradeproData.eventFilter}
                            onChange={(e) => updateTradeproFilter("eventFilter", e.target.value)}
                          />
                        </th>
                        <th colSpan="2">
                          <button
                            className="btn btn-default btn-sm"
                            onClick={clearTradeproFilters}
                            title="Clear filters"
                          >
                            Clear
                          </button>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {tradeproData.loading ? (
                        Array.from({ length: 6 }).map((_, index) => (
                          <tr key={index} className="skeleton-row">
                            <td><div className="skeleton-box" /></td>
                            <td><div className="skeleton-box" /></td>
                            <td><div className="skeleton-box" /></td>
                            <td><div className="skeleton-box" style={{ width: "30px" }} /></td>
                            <td><div className="skeleton-box" style={{ width: "30px" }} /></td>
                          </tr>
                        ))
                      ) : tradeproPaginated.length > 0 ? (
                        tradeproPaginated.map((workflow, index) => (
                          <tr key={workflow.id}>
                            <td style={{ textAlign: "center", verticalAlign: "middle" }}>
                              {(tradeproData.page - 1) * limit + index + 1}
                            </td>
                            <td style={{ verticalAlign: "middle" }}>
                              {workflow.workflow_name}
                            </td>
                            <td style={{ verticalAlign: "middle" }}>
                              {getEventDisplay(workflow)}
                            </td>
                            <td>
                              <PermissionWrapper module="workflow_order" action="edit">
                                <Link
                                  to={`/WorkflowTradepro/Order/edit/${workflow.id}`}
                                  className="btn btn-warning btn-xs"
                                >
                                  <FontAwesomeIcon icon={faPencil} />
                                </Link>
                              </PermissionWrapper>
                            </td>
                            <td>
                              <PermissionWrapper module="workflow_order" action="delete">
                                <button
                                  onClick={() => handleTradeproDelete(workflow.id)}
                                  className="btn btn-danger btn-xs"
                                >
                                  <FontAwesomeIcon icon={faTrash} />
                                </button>
                              </PermissionWrapper>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="5" className="text-center">
                            No Tradepro workflows match your search criteria.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="p-3">
                  No Tradepro records found.{" "}
                  <Link to="/WorkflowTradepro/Order/add/">Add a new workflow</Link>
                </div>
              )}
            </>
          )}

          {/* Streetfolio Tab Content */}
          {activeTab === "streetfolio" && (
            <>
              {streetfolioData.workflows.length > 0 || streetfolioData.loading ? (
                <div className="table-responsive sticky-table-container">
                  <style>
                    {`
                      .sticky-seo-table thead tr:first-child th {
                        position: sticky;
                        top: 0;
                        z-index: 20;
                        background: #f8f9fa;
                       }

                      .sticky-seo-table thead tr:nth-child(2) th {
                        position: sticky;
                        top: 25px; /* Adjust if needed based on first header height */
                        z-index: 19;
                        background: white;
                       }

                      .sticky-table-container {
                        max-height: calc(100vh - 268px); /* Adjust height if needed */
                        overflow: auto;
                       }

                      .sticky-seo-table th {
                        vertical-align: top;
                       }
                    `}
                  </style>
                  <table className="table table-condensed table-bordered bg-white sticky-seo-table">
                    <thead>
                      <tr>
                        <th style={{ textAlign: "center" }}>#</th>
                        <th>Workflow Name</th>
                        <th>Event</th>
                        <th style={{ width: "1%" }}>Edit</th>
                        <th style={{ width: "1%" }}>Delete</th>
                      </tr>
                      <tr>
                        <th style={{ textAlign: "center" }}></th>
                        <th>
                          <input
                            type="text"
                            className="form-control input-sm"
                            placeholder="Search workflow name..."
                            value={streetfolioData.workflowNameFilter}
                            onChange={(e) => updateStreetfolioFilter("workflowNameFilter", e.target.value)}
                          />
                        </th>
                        <th>
                          <input
                            type="text"
                            className="form-control input-sm"
                            placeholder="Search event..."
                            value={streetfolioData.eventFilter}
                            onChange={(e) => updateStreetfolioFilter("eventFilter", e.target.value)}
                          />
                        </th>
                        <th colSpan="2">
                          <button
                            className="btn btn-default btn-sm"
                            onClick={clearStreetfolioFilters}
                            title="Clear filters"
                          >
                            Clear
                          </button>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {streetfolioData.loading ? (
                        Array.from({ length: 6 }).map((_, index) => (
                          <tr key={index} className="skeleton-row">
                            <td><div className="skeleton-box" /></td>
                            <td><div className="skeleton-box" /></td>
                            <td><div className="skeleton-box" /></td>
                            <td><div className="skeleton-box" style={{ width: "30px" }} /></td>
                            <td><div className="skeleton-box" style={{ width: "30px" }} /></td>
                          </tr>
                        ))
                      ) : streetfolioPaginated.length > 0 ? (
                        streetfolioPaginated.map((workflow, index) => (
                          <tr key={workflow.id}>
                            <td style={{ textAlign: "center", verticalAlign: "middle" }}>
                              {(streetfolioData.page - 1) * limit + index + 1}
                            </td>
                            <td style={{ verticalAlign: "middle" }}>
                              {workflow.workflow_name}
                            </td>
                            <td style={{ verticalAlign: "middle" }}>
                              {getEventDisplay(workflow)}
                            </td>
                            <td>
                              <PermissionWrapper module="workflow_order" action="edit">
                                <Link
                                  to={`/WorkflowStreetfolio/Order/edit/${workflow.id}`}
                                  className="btn btn-warning btn-xs"
                                >
                                  <FontAwesomeIcon icon={faPencil} />
                                </Link>
                              </PermissionWrapper>
                            </td>
                            <td>
                              <PermissionWrapper module="workflow_order" action="delete">
                                <button
                                  onClick={() => handleStreetfolioDelete(workflow.id)}
                                  className="btn btn-danger btn-xs"
                                >
                                  <FontAwesomeIcon icon={faTrash} />
                                </button>
                              </PermissionWrapper>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="5" className="text-center">
                            No Streetfolio workflows match your search criteria.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="p-3">
                  No Streetfolio records found.{" "}
                  <Link to="/WorkflowStreetfolio/Order/add/">Add a new workflow</Link>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Modal component */}
      <div id="myModal" className="modal">
        <div className="modal-dialog panel panel-default panel-border top">
          <div className="modal-content">
            <div id="myModalContent"></div>
          </div>
        </div>
      </div>

      {/* Scroll to top */}
      <a href="#" data-click="scroll-top" className="btn-scroll-top fade">
        <i className="ti-arrow-up"></i>
      </a>
    </div>
  );
}

export default WorkflowOrder;